// ProposalCard — summary card for one governance proposal.
import React from "react";
import { Clock, CheckCircle2, XCircle, BadgeCheck, Wrench } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tGov } from "../../i18n/governance";

const fmt = (n) => Number(n).toLocaleString();

export const shortWallet = (w) =>
  !w ? "" : w === "team" ? null : `${w.slice(0, 6)}…${w.slice(-4)}`;

export const countdownText = (endsAt, t) => {
  const ms = new Date(endsAt) - Date.now();
  if (ms <= 0) return t("closed");
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const VoteBars = ({ p, compact = false }) => {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const rows = [
    { label: t("yes"), pct: p.yes_pct, votes: p.yes_votes, color: "#34d399" },
    { label: t("no"), pct: p.no_pct, votes: p.no_votes, color: "#fb7185" },
  ];
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-bold" style={{ color: r.color }}>{r.label}</span>
            <span className="font-mono text-white/60">
              {r.pct}%{!compact && ` · ${fmt(r.votes)} R₿L`}
            </span>
          </div>
          <div className="mt-0.5 h-2 overflow-hidden rounded-full bg-white/[0.06]">
            <div className="h-full rounded-full transition-[width] duration-700"
              style={{ width: `${r.pct}%`, background: r.color }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const StatusPill = ({ p }) => {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  if (p.status === "active") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-[0.18em] text-cyan-300">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400/70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
        </span>
        {t("active")}
      </span>
    );
  }
  if (p.implemented) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-[0.18em] text-amber-300">
        <Wrench className="h-3 w-3" /> {t("implemented")}
      </span>
    );
  }
  const passed = p.result === "passed";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-[0.18em] ${
      passed ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-rose-400/40 bg-rose-400/10 text-rose-300"
    }`}>
      {passed ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {passed ? t("passed") : t("rejected")}
    </span>
  );
};

export default function ProposalCard({ proposal: p, myVote, onOpen }) {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const proposer = shortWallet(p.proposer_wallet);

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-purple-400/40"
      data-testid={`gov-proposal-card-${p.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill p={p} />
        {p.is_official && (
          <span className="inline-flex items-center gap-1 rounded-full border border-purple-400/40 bg-purple-400/10 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-[0.18em] text-purple-300">
            <BadgeCheck className="h-3 w-3" /> {t("official")}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-white/45">
          <Clock className="h-3 w-3" />
          {p.status === "active" ? `${t("closesIn")} ${countdownText(p.ends_at, t)}` : (p.ends_at || "").slice(0, 10)}
        </span>
      </div>

      <h3 className="mt-3 text-base font-black text-white">{p.title}</h3>
      <p className="mt-0.5 text-[11px] text-white/45">
        {t("proposedBy")}: <span className="font-mono">{proposer || t("team")}</span>
        <span className="ml-2 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] uppercase text-white/50">
          {t(`type_${p.type}`)}
        </span>
      </p>

      <div className="mt-4">
        <VoteBars p={p} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[10px] text-white/40">
          {t("quorum")}: {p.quorum_pct}% · {fmt(p.total_voters)} 🗳
          {myVote && (
            <span className={`ml-2 font-black ${myVote.vote === "yes" ? "text-emerald-300" : "text-rose-300"}`}>
              {t("yourVote")}: {myVote.vote === "yes" ? `✅ ${t("yes")}` : `❌ ${t("no")}`}
            </span>
          )}
        </p>
        <button onClick={() => onOpen(p.id)} data-testid={`gov-open-proposal-${p.id}`}
          className="rounded-full border border-purple-400/40 bg-purple-400/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-purple-200 transition-all hover:bg-purple-400/20">
          {p.status === "active" && !myVote ? t("vote") : t("viewDetails")}
        </button>
      </div>
    </div>
  );
}
